import type { PresenceState as DotState } from '@cyphex/ui';
import type { ActivityState, ManualState } from './types';

export interface ActivityMeta {
  label: string;
  /**
   * The design-system state this activity presents as. Colour AND glyph both
   * come from `PRESENCE_META` via `<PresenceDot>` — this table deliberately
   * carries no glyph of its own. A second glyph vocabulary here is what once
   * put two different marks ("✓ ✓ Available") on one fact.
   */
  dot: DotState;
  /** Whether a caller can reach this person without piercing DND. */
  reachable: boolean;
}

/**
 * The 9+ automatic states. `dot` maps each one onto a Wave-1 presence token so
 * no new colour is invented; `label` carries the meaning the dot cannot.
 *
 * Teams draws TWO different red badges, and both are already in the Wave-1
 * vocabulary, so this table reaches for the right one rather than inventing a
 * tenth token:
 *   - Busy / In a call / In a meeting -> a plain filled red circle
 *     (`busy`, `inAMeeting`), no glyph.
 *   - Presenting / Screen sharing -> the do-not-disturb badge (`dnd`), a red
 *     circle wearing a white bar, so "on stage, do not interrupt" survives
 *     a 10px dot and a viewer who cannot separate the two reds.
 *
 * Colour alone cannot separate Presenting from Do not disturb — they are the
 * same badge on purpose. `label` is what separates them, so any surface where
 * the dot is the ONLY presence affordance must pass it through as
 * `<PresenceDot label={ACTIVITY_META[state].label}>`; otherwise the badge
 * announces a presenting colleague as "Do not disturb".
 */
export const ACTIVITY_META: Record<ActivityState, ActivityMeta> = {
  available: { label: 'Available', dot: 'available', reachable: true },
  busy: { label: 'Busy', dot: 'busy', reachable: true },
  inAMeeting: { label: 'In a meeting', dot: 'inAMeeting', reachable: false },
  inACall: { label: 'In a call', dot: 'busy', reachable: false },
  presenting: { label: 'Presenting', dot: 'dnd', reachable: false },
  screenSharing: { label: 'Screen sharing', dot: 'dnd', reachable: false },
  away: { label: 'Away', dot: 'away', reachable: true },
  beRightBack: { label: 'Be right back', dot: 'brb', reachable: true },
  doNotDisturb: { label: 'Do not disturb', dot: 'dnd', reachable: false },
  outOfOffice: { label: 'Out of office', dot: 'oof', reachable: false },
  offline: { label: 'Offline', dot: 'offline', reachable: false },
};

/** Manual override -> the automatic state it presents as. */
export const MANUAL_PRESENTS_AS: Record<ManualState, ActivityState> = {
  appearAvailable: 'available',
  appearBusy: 'busy',
  appearDnd: 'doNotDisturb',
  appearAway: 'away',
  beRightBack: 'beRightBack',
  appearOffline: 'offline',
};

export const MANUAL_LABEL: Record<ManualState, string> = {
  appearAvailable: 'Available',
  appearBusy: 'Busy',
  appearDnd: 'Do not disturb',
  appearAway: 'Away',
  beRightBack: 'Be right back',
  appearOffline: 'Appear offline',
};

/** Order shown in the presence picker. */
export const MANUAL_ORDER: readonly ManualState[] = [
  'appearAvailable', 'appearBusy', 'appearDnd', 'beRightBack', 'appearAway', 'appearOffline',
] as const;

export function activityMeta(state: ActivityState): ActivityMeta {
  return ACTIVITY_META[state];
}

export function manualMeta(state: ManualState): ActivityMeta {
  return ACTIVITY_META[MANUAL_PRESENTS_AS[state]];
}
